import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BrowserMultiFormatReader } from "@zxing/library";
import { Scan, Search, Trash2 } from "lucide-react";
import axios from "axios";
import { useNotification } from "../../hooks/useNotification";
import { useCurrencyFormatter } from "../../utils/formatCurrency";
import ColourMeta from "../Common/ColourMeta";

const buildEmptyItem = () => ({
  id: Date.now() + Math.random(),
  quantity: 1,
  price: "",
  serialNumber: "",
  selectedVariant: "",
  variantSearch: "",
  showVariantDropdown: false,
  variantMeta: null,
});

const parsePriceInput = (value) => {
  const normalized = String(value ?? "")
    .replace(/,/g, ".")
    .replace(/[^\d.]/g, "")
    .trim();

  if (!normalized) return 0;

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getVariantDisplayName = (variant) => {
  if (!variant) return "";

  const modelCode = variant.product_model_code?.trim();
  const fallbackModelCode = variant.productModel?.product_model_code?.trim();
  const description = variant.product_model_description?.trim();

  return modelCode || fallbackModelCode || description || "";
};

const isBrokenModelCode = (variant) => {
  const modelCode = String(
    variant?.product_model_code || variant?.productModel?.product_model_code || "",
  ).trim();

  return !modelCode || modelCode === "000000";
};

const getVariantSearchLabel = (variant) => {
  if (!variant) return "";

  return getVariantDisplayName(variant);
};

const normalizeVariantSearchValue = (value) =>
  String(value || "").trim().toLowerCase();

const findExactVariantMatch = (variants, searchValue) => {
  const normalizedSearch = normalizeVariantSearchValue(searchValue);
  if (!normalizedSearch) return null;

  const exactMatches = (variants || []).filter(
    (variant) =>
      normalizeVariantSearchValue(getVariantSearchLabel(variant)) ===
      normalizedSearch,
  );

  return exactMatches.length === 1 ? exactMatches[0] : null;
};

function SaleForm({
  mode = "sale",
  requireReceipt = false,
  onCancel,
  onSubmit,
  submitting,
  formError,
}) {
  const { t } = useTranslation();
  const { showWarning } = useNotification();
  const { format: formatCurrency, currency } = useCurrencyFormatter();
  const isReturnMode = mode === "return";
  const [formData, setFormData] = useState({
    items: [buildEmptyItem()],
    receiptNumber: "",
    receiptImage: null,
    notes: "",
  });

  const [receiptPreview, setReceiptPreview] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef(null);
  const codeReader = useRef(new BrowserMultiFormatReader());

  const [allVariants, setAllVariants] = useState({});
  const [loadingVariantsByItem, setLoadingVariantsByItem] = useState({});
  const searchTimeoutsRef = useRef({});
  const latestSearchRef = useRef({});

  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }

      setFormData((prev) => ({ ...prev, receiptImage: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveReceipt = () => {
    setFormData((prev) => ({ ...prev, receiptImage: null }));
    setReceiptPreview(null);
  };

  const stopBarcodeScan = useCallback(() => {
    setIsScanning(false);
    codeReader.current.reset();
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
  }, []);

  const startBarcodeScan = useCallback(
    async (itemIndex) => {
      setIsScanning(itemIndex);
      try {
        const result = await codeReader.current.decodeOnceFromVideoDevice(
          undefined,
          videoRef.current,
        );
        if (result) {
          setFormData((prev) => {
            const newItems = [...prev.items];
            newItems[itemIndex].serialNumber = result.getText();
            return { ...prev, items: newItems };
          });
          stopBarcodeScan();
        }
      } catch (err) {
        console.error("Barcode scan failed:", err);
        stopBarcodeScan();
      }
    },
    [stopBarcodeScan],
  );

  useEffect(() => {
    if (isScanning !== false) {
      startBarcodeScan(isScanning);
    }
    return () => {
      if (isScanning !== false) {
        stopBarcodeScan();
      }
    };
  }, [isScanning, startBarcodeScan, stopBarcodeScan]);

  const fetchVariantOptions = useCallback(async (itemId, query = "") => {
    const normalizedQuery = String(query || "").trim();
    latestSearchRef.current[itemId] = normalizedQuery;

    setLoadingVariantsByItem((prev) => ({ ...prev, [itemId]: true }));

    try {
      const response = await axios.get("/api/product-hierarchy/variants", {
        params: {
          q: normalizedQuery || undefined,
          limit: 30,
        },
      });

      if (
        response.data.success &&
        latestSearchRef.current[itemId] === normalizedQuery
      ) {
        setAllVariants((prev) => ({
          ...prev,
          [itemId]: (response.data.data || []).filter(
            (variant) => !isBrokenModelCode(variant),
          ),
        }));
      }
    } catch (error) {
      console.error("Failed to load product options:", error);
      if (latestSearchRef.current[itemId] === normalizedQuery) {
        setAllVariants((prev) => ({
          ...prev,
          [itemId]: [],
        }));
      }
    } finally {
      if (latestSearchRef.current[itemId] === normalizedQuery) {
        setLoadingVariantsByItem((prev) => ({ ...prev, [itemId]: false }));
      }
    }
  }, []);

  const queueVariantFetch = useCallback(
    (itemId, query = "") => {
      if (searchTimeoutsRef.current[itemId]) {
        clearTimeout(searchTimeoutsRef.current[itemId]);
      }

      searchTimeoutsRef.current[itemId] = setTimeout(() => {
        fetchVariantOptions(itemId, query);
      }, 250);
    },
    [fetchVariantOptions],
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".searchable-dropdown")) {
        setFormData((prev) => ({
          ...prev,
          items: prev.items.map((item) => ({
            ...item,
            showVariantDropdown: false,
          })),
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const currentTimeouts = searchTimeoutsRef.current;
    return () => {
      Object.values(currentTimeouts).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  const handleVariantSelect = (index, variant) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        selectedVariant: variant.product_id,
        variantSearch: getVariantSearchLabel(variant),
        showVariantDropdown: false,
        variantMeta: {
          categoryName:
            variant.productGroup?.product_group_description || "",
          productName: variant.product_description || "",
          modelName: getVariantDisplayName(variant),
          modelDescription: variant.product_model_description || "",
          colour: variant.colour || "",
          displayName: getVariantSearchLabel(variant),
          productId: variant.product_id,
        },
      };
      return { ...prev, items: newItems };
    });
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      const nextItem = { ...newItems[index], [field]: value };

      if (field === "variantSearch") {
        nextItem.showVariantDropdown = true;
        nextItem.selectedVariant = "";
        nextItem.variantMeta = null;
      }

      newItems[index] = nextItem;
      return { ...prev, items: newItems };
    });
  };

  const addItem = () => {
    if (formData.items.length < 30) {
      setFormData((prev) => ({
        ...prev,
        items: [...prev.items, buildEmptyItem()],
      }));
    }
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData((prev) => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }));
    }
  };

  const grandTotal = useMemo(
    () =>
      formData.items.reduce((sum, item) => {
        const qty = Number.parseInt(item.quantity, 10) || 0;
        const price = parsePriceInput(item.price);
        return sum + qty * price;
      }, 0),
    [formData.items],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const invalidItemIndex = formData.items.findIndex(
      (item) =>
        !String(item.variantSearch || "").trim() ||
        !Number.isInteger(Number.parseInt(item.selectedVariant, 10)),
    );
    if (invalidItemIndex !== -1) {
      const message = t("se.saleForm.modelValidationError", {
        n: invalidItemIndex + 1,
      });
      showWarning(message);
      return;
    }

    if (requireReceipt && !formData.receiptImage) {
      const message = t("se.saleForm.receiptRequiredForReturn");
      showWarning(message);
      return;
    }

    const saleData = {
      items: formData.items.map((item) => ({
        product_id: item.selectedVariant
          ? Number.parseInt(item.selectedVariant, 10)
          : null,
        product_name:
          item.variantMeta?.productName || item.variantSearch.trim() || "",
        category_name: item.variantMeta?.categoryName || "",
        model_name: item.variantMeta?.modelName || item.variantSearch || "",
        variant_name: item.variantMeta?.displayName || item.variantSearch || "",
        quantity: Number.parseInt(item.quantity, 10) || 1,
        unit_price: parsePriceInput(item.price),
        serial_number: item.serialNumber || null,
      })),
      notes: formData.notes,
      receiptNumber: formData.receiptNumber,
      receiptFile: formData.receiptImage,
    };

    onSubmit(saleData);
  };

  return (
    <div className="sale-form-container sale-mobile-shell">
      <form id="sale-form" onSubmit={handleSubmit} className="sale-form">
        {formData.items.map((item, index) => {
          const filteredVariants = (allVariants[item.id] || []).filter(
            (variant) => !isBrokenModelCode(variant),
          );
          const isLoadingVariants = Boolean(loadingVariantsByItem[item.id]);

          return (
            <div key={item.id} className="sale-item-section sale-item-grid-section">
              <div className="sale-item-section-header">
                <h4>{t("se.saleForm.itemTitle", { n: index + 1 })}</h4>
                {formData.items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="sale-item-remove-btn"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    {t("se.saleForm.remove")}
                  </button>
                )}
              </div>

              <div className="form-group sale-entry-grid-model">
                <label className="sale-item-label sale-item-label-model">
                  {t("se.saleForm.modelLabel")}
                  <span className="required-asterisk" aria-hidden="true">*</span>
                </label>
                <div className="searchable-dropdown">
                  <div className="searchable-dropdown-input">
                    <Search size={18} className="searchable-dropdown-icon" />
                    <input
                      type="text"
                      value={item.variantSearch}
                      onChange={(e) => {
                        handleItemChange(index, "variantSearch", e.target.value);
                        queueVariantFetch(item.id, e.target.value);
                      }}
                      onFocus={() => {
                        handleItemChange(index, "showVariantDropdown", true);
                        queueVariantFetch(item.id, item.variantSearch);
                      }}
                      onBlur={() => {
                        const exactMatch = findExactVariantMatch(
                          filteredVariants,
                          item.variantSearch,
                        );

                        if (exactMatch) {
                          handleVariantSelect(index, exactMatch);
                        }
                      }}
                      onKeyDown={(e) => {
                        const exactMatch = findExactVariantMatch(
                          filteredVariants,
                          item.variantSearch,
                        );

                        if (e.key === "Enter") {
                          e.preventDefault();

                          if (exactMatch) {
                            handleVariantSelect(index, exactMatch);
                          } else if (filteredVariants.length === 1) {
                            handleVariantSelect(index, filteredVariants[0]);
                          }
                        } else if (e.key === "Escape") {
                          handleItemChange(index, "showVariantDropdown", false);
                        }
                      }}
                      placeholder={
                        isLoadingVariants
                          ? t("se.saleForm.loadingModels")
                          : t("se.saleForm.searchModelQuick")
                      }
                      className="searchable-dropdown-text-input"
                    />
                  </div>
                  {item.showVariantDropdown && (
                    <div className="dropdown-list">
                      {isLoadingVariants ? (
                        <div className="dropdown-item disabled">
                          {t("se.saleForm.loadingModels")}
                        </div>
                      ) : filteredVariants.length > 0 ? (
                        filteredVariants.slice(0, 30).map((variant) => (
                          <div
                            key={variant.product_id}
                            className="dropdown-item"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleVariantSelect(index, variant);
                            }}
                          >
                            <div className="sale-model-option-name">
                              {getVariantSearchLabel(variant)}
                            </div>
                            <div className="sale-model-option-meta">
                              <ColourMeta
                                label={`${t("se.saleForm.colourHelperLabel")} `}
                                colour={variant.colour}
                              />
                              {variant.colour && variant.product_model_description
                                ? " · "
                                : ""}
                              {variant.product_model_description || ""}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item disabled">
                          {t("se.saleForm.noModelsFound")}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {item.variantMeta && (
                  <div className="sale-model-meta">
                    <div>
                      {item.variantMeta.colour ? (
                        <ColourMeta
                          label={`${t("se.saleForm.colourHelperLabel")} `}
                          colour={item.variantMeta.colour}
                        />
                      ) : (
                        <>
                          <strong>{t("se.saleForm.colourHelperLabel")}</strong>{" "}
                          {t("se.saleForm.notAvailable")}
                        </>
                      )}
                    </div>
                    <div>
                      <strong>{t("se.saleForm.modelDescriptionHelperLabel")}</strong>{" "}
                      {item.variantMeta.modelDescription ||
                        t("se.saleForm.notAvailable")}
                    </div>
                  </div>
                )}
              </div>

              <div className="sale-entry-inline-row">
                <div className="form-group sale-entry-grid-qty">
                  <label>
                    {t("se.saleForm.quantityLabel")}
                    <span className="required-asterisk" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      handleItemChange(index, "quantity", e.target.value)
                    }
                    placeholder="1"
                    required
                  />
                  {isReturnMode && (
                    <small className="quantity-hint">
                      {t("se.saleForm.returnQuantityHint")}
                    </small>
                  )}
                </div>

                <div className="form-group sale-entry-grid-price">
                  <label>
                    {t("se.saleForm.unitPriceLabel", { currency })}
                    <span className="required-asterisk" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={item.price}
                    onChange={(e) =>
                      handleItemChange(index, "price", e.target.value)
                    }
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t("se.saleForm.serialLabel")}</label>
                <div className="serial-input-container">
                  <input
                    type="text"
                    value={item.serialNumber}
                    onChange={(e) =>
                      handleItemChange(index, "serialNumber", e.target.value)
                    }
                    placeholder={t("se.saleForm.serialPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => setIsScanning(index)}
                    className="scan-btn"
                    title="Scan barcode"
                  >
                    <Scan size={20} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {formData.items.length < 30 && (
          <button
            type="button"
            onClick={addItem}
            className="sale-add-item-btn"
          >
            <span style={{ fontSize: "18px" }}>+</span>{" "}
            {t("se.saleForm.addAnotherItem")}
          </button>
        )}

        <div className="sale-receipt-card">
          <h3 className="sale-receipt-card-title">
            {t("se.saleForm.receiptSectionTitle")}
          </h3>

          <div className="form-group">
            <div className="receipt-upload-container">
              {!receiptPreview ? (
                <label className="receipt-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleReceiptUpload}
                    style={{ display: "none" }}
                  />
                  <div className="receipt-upload-box sale-receipt-upload-box">
                    <span>{t("se.saleForm.clickToUpload")}</span>
                    <small>{t("se.saleForm.uploadHint")}</small>
                  </div>
                </label>
              ) : (
                <div className="receipt-preview">
                  <img src={receiptPreview} alt="Receipt preview" />
                  <button
                    type="button"
                    onClick={handleRemoveReceipt}
                    className="receipt-remove-btn"
                  >
                    {t("se.saleForm.removeReceipt")}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{t("se.saleForm.receiptNumberLabel")}</label>
            <input
              type="text"
              value={formData.receiptNumber}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, receiptNumber: e.target.value }))
              }
              placeholder={t("se.saleForm.receiptNumberPlaceholder")}
            />
          </div>

          <div className="form-group">
            <label>{t("se.saleForm.notesLabel")}</label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder={t("se.saleForm.notesPlaceholder")}
              rows="2"
            />
          </div>
        </div>

        {formError && <div className="form-error">{formError}</div>}
      </form>

      <div className="sale-mobile-footer">
        <div
          className={`sale-mobile-footer-total ${
            isReturnMode ? "return-mode" : "sale-mode"
          }`}
        >
          <span>{t("se.saleForm.batchTotalLabel")}</span>
          <strong>{formatCurrency(grandTotal, new Date(), currency)}</strong>
        </div>
        <div className="sale-mobile-footer-actions">
          <button type="button" onClick={onCancel} className="btn-cancel sale-mobile-cancel">
            {t("se.saleForm.cancel")}
          </button>
          <button type="submit" form="sale-form" className="btn-submit sale-mobile-submit" disabled={submitting}>
            {submitting
              ? t("se.saleForm.saving")
              : isReturnMode
                ? t("se.saleForm.saveReturnSale")
                : t("se.saleForm.saveSale")}
          </button>
        </div>
      </div>

      {isScanning !== false && (
        <div className="barcode-scan-overlay">
          <div className="barcode-scan-modal">
            <h3>{t("se.saleForm.scanBarcode")}</h3>
            <video ref={videoRef} className="barcode-video" />
            <div className="scan-actions">
              <button onClick={stopBarcodeScan} className="btn-cancel">
                {t("se.saleForm.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SaleForm;
